import React, { useState } from 'react';
import { graphql, createFragmentContainer } from 'react-relay';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Link } from 'dibs-elements/exports/Link';
import { Button } from 'dibs-elements/exports/Button';
import { Carousel } from 'dibs-carousel';
import { truncateString } from './helpers/truncateStringHelper';
import { readMore, aboutSeller, close } from '../messages/pdpBioMessages';

import styles from './PdpSharedDealerDetailsBio.scss';

const maxCharLength = 300;

const PdpRespDealerDetailsBioComponent = ({ seller }) => {
    const { isRewardsTier2, liveStorefrontProfile } = seller || {};
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [currentStepIndex, setStepIndex] = useState(0);

    if (!liveStorefrontProfile || !isRewardsTier2) {
        return null;
    }

    const { aboutUsUnbranded: aboutUs, headshots } = liveStorefrontProfile;
    const headshot = (headshots && headshots[0]) || {};
    const { status: headshotStatus, path: headshotPath } = headshot;
    const showHeadshot = headshotStatus === 'ACTIVE' && !!headshotPath;
    const galleryAlbum = get(liveStorefrontProfile, 'galleryAlbum.mediaList');
    const galleryPhotos = galleryAlbum
        ? galleryAlbum.filter(photo => get(photo, 'element.path'))
        : null;
    let currentPhoto;

    const renderItem = () => {
        currentPhoto = get(galleryPhotos[currentStepIndex], 'element');
        return (
            <img key={currentPhoto.path} className={styles.carouselImage} src={currentPhoto.path} />
        );
    };

    return (
        aboutUs && (
            <React.Fragment>
                <div className={styles.container}>
                    {showHeadshot && (
                        <span className={styles.headshotContainer}>
                            <img className={styles.headshot} src={headshotPath} />
                        </span>
                    )}
                    <span className={styles.textContainer}>
                        <p className={styles.aboutUs}>{truncateString(aboutUs, maxCharLength)}</p>
                        {aboutUs.length > maxCharLength && (
                            <Link className={styles.readMore} onClick={() => setModalIsOpen(true)}>
                                {readMore}
                            </Link>
                        )}
                    </span>
                </div>
                <ModalContainer isOpen={modalIsOpen} onClose={() => setModalIsOpen(false)}>
                    <ModalCloseButton onClick={() => setModalIsOpen(false)} />
                    <BarHeader title={aboutSeller} />
                    <ModalBody className={styles.modalBody}>
                        {galleryPhotos && (
                            <Carousel
                                enableKeyboardControl
                                renderItem={() => renderItem()}
                                totalItems={galleryPhotos.length}
                                onIndexChange={({ index }) => setStepIndex(index)}
                                classNames={{ button: styles.mobileButton }}
                            />
                        )}
                        <div
                            className={styles.modalText}
                            dangerouslySetInnerHTML={{ __html: aboutUs }}
                        />
                        <Button
                            className={styles.modalButton}
                            onClick={() => setModalIsOpen(false)}
                        >
                            {close}
                        </Button>
                    </ModalBody>
                </ModalContainer>
            </React.Fragment>
        )
    );
};

PdpRespDealerDetailsBioComponent.propTypes = {
    seller: PropTypes.object.isRequired,
};

export const PdpRespDealerDetailsBio = createFragmentContainer(PdpRespDealerDetailsBioComponent, {
    seller: graphql`
        fragment PdpRespDealerDetailsBio_seller on Seller {
            serviceId
            isRewardsTier2: isAnchorListMember(listName: "SELLER_REWARDS_2")
            liveStorefrontProfile {
                aboutUsUnbranded
                headshots: photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                    status
                    path
                }
                galleryAlbum {
                    mediaList {
                        sortOrder
                        element {
                            ... on Photo {
                                path
                            }
                        }
                    }
                }
            }
        }
    `,
});
